import React, { useState } from 'react';
import { MessageSquare, Building2, FileText, CreditCard, Calculator, Briefcase } from 'lucide-react';

const EasySteps = () => {
  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    {
      number: 1,
      icon: MessageSquare,
      title: 'Free Consultation with Business Setup Advisor',
      description: 'Arrange a face-to-face meeting or a Zoom call with one of our advisors. We recommend having a basic understanding of your business activity, the number of shareholders, your target customers in the UAE, and office space requirements. Even if you need assistance, our team is ready to support you in finalizing your business plan.',
    },
    {
      number: 2,
      icon: Building2,
      title: 'Selecting the Right Jurisdiction',
      description: 'We will assist you in selecting the right jurisdiction for your company in the UAE, tailored to your unique needs and target market. You can establish your business in UAE Mainland for maximum operational flexibility, leverage the benefits of a Free Zone setup, or consider an offshore setup for international business.',
    },
    {
      number: 3,
      icon: FileText,
      title: 'Applying for Trade License',
      description: 'Prepare all your registration documents, including your Application Form, Memorandum of Association, Certificate of Incorporation, passport copies, and more. Requirements differ depending on the license type and jurisdiction in the UAE, so our team will gladly assist you throughout the process.',
    },
    {
      number: 4,
      icon: CreditCard,
      title: 'Initiating Visa and Emirates ID Applications',
      description: 'Before relocating to the UAE, it is essential to apply for the necessary visas for yourself, your employees, and any dependents. The specific visa options and availability will depend on the jurisdiction you have selected. Our team will guide you through the visa application process.',
    },
    {
      number: 5,
      icon: Calculator,
      title: 'Register for Corporate Tax',
      description: 'The UAE Corporate Tax is a federal tax applied to business profits, effective from 1 June 2023. It is charged at 9% on taxable profits exceeding AED 375,000, while profits up to this threshold are taxed at 0%, supporting startups and SMEs. Most businesses and professional activities are covered, with certain exemptions for free zone entities that meet specific conditions. Corporate Tax aims to align the UAE with global tax standards while maintaining a competitive business environment.',
    },
    {
      number: 6,
      icon: Briefcase,
      title: 'Opening a Corporate Bank Account',
      description: 'A corporate bank account is necessary to conduct business in the UAE. You can choose from several national, international, and digital banks, depending on your financial requirements. Our team of experts will help you determine the best bank for your needs and prepare the requirements for submission.',
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gray-800">Easy Steps</span><br />
            <span className="text-gray-600">To Start Your Business in the UAE</span>
          </h2>
        </div>

        {/* Desktop View - Side by Side Layout */}
        <div className="hidden md:grid md:grid-cols-2 gap-8">
          {/* Left Side - Steps Selection */}
          <div className="space-y-3">
            {steps.map((step) => {
              const IconComponent = step.icon;
              const isActive = activeStep === step.number;
              return (
                <div
                  key={step.number}
                  className={`relative cursor-pointer group transition-all duration-300 ${
                    isActive ? 'scale-102' : ''
                  }`}
                  onClick={() => setActiveStep(step.number)}
                  onMouseEnter={() => setActiveStep(step.number)}
                >
                  <div className={`flex items-center gap-4 p-4 rounded-xl ${
                    isActive 
                      ? 'bg-gradient-to-r from-[#8B1B4D] to-[#DC143C] shadow-xl' 
                      : 'bg-white shadow-md hover:shadow-lg'
                  } transition-all`}>
                    {/* Number Circle */}
                    <div className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-bold text-2xl transition-all ${
                      isActive 
                        ? 'bg-white text-[#8B1B4D]' 
                        : 'bg-gradient-to-br from-[#8B1B4D] to-[#DC143C] text-white'
                    }`}>
                      {step.number}
                    </div>
                    {/* Icon */}
                    <div className={`p-2 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-white/20' 
                        : 'bg-gray-50'
                    }`}>
                      <IconComponent className={isActive ? 'text-white' : 'text-[#8B1B4D]'} size={28} />
                    </div>
                    {/* Step Title */}
                    <div className="flex-1">
                      <h4 className={`font-bold text-base leading-tight ${isActive ? 'text-white' : 'text-gray-800'}`}>
                        {step.title}
                      </h4>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Side - Active Step Detail */}
          <div className="sticky top-32 self-start">
            <div className="bg-white p-8 rounded-2xl shadow-2xl border-2 border-gray-100">
              <div className="flex items-start gap-5 mb-5">
                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-[#8B1B4D] to-[#DC143C] rounded-full flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                  {activeStep}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">
                    {steps[activeStep - 1].title}
                  </h3>
                </div>
              </div>
              <p className="text-gray-700 text-base leading-relaxed">
                {steps[activeStep - 1].description}
              </p>
            </div>
          </div>
        </div>

        {/* Mobile View - Original Layout */}
        <div className="md:hidden">
          {/* Step Selector */}
          <div className="flex justify-center gap-3 mb-8 flex-wrap">
            {steps.map((step) => (
              <button
                key={step.number}
                onClick={() => setActiveStep(step.number)}
                className={`w-16 h-16 rounded-full font-bold text-xl transition-all shadow-lg ${
                  activeStep === step.number
                    ? 'bg-gradient-to-br from-[#8B1B4D] to-[#DC143C] text-white scale-110'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {step.number}
              </button>
            ))}
          </div>

          {/* Active Step Detail */}
          <div className="bg-white p-8 rounded-2xl shadow-2xl border-2 border-gray-100">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-[#8B1B4D] to-[#DC143C] rounded-full flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                {activeStep}
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {steps[activeStep - 1].title}
                </h3>
                <p className="text-gray-700 text-base leading-relaxed">
                  {steps[activeStep - 1].description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EasySteps;